/**
 * ProgressBar - Minimalist loading bar with smooth animations
 */

import { UIColors, UIAlpha, UIBorderRadius, UIAnimation } from '../UIDesignTokens';

export default class ProgressBar {
    constructor(scene, x, y, config = {}) {
        this.scene = scene;
        this.x = x;
        this.y = y;
        
        this.config = {
            width: config.width ?? 300,
            height: config.height ?? 6,
            bgColor: config.bgColor ?? UIColors.gray800,
            fillColor: config.fillColor ?? UIColors.primary,
            glowColor: config.glowColor ?? UIColors.primaryLight,
            cornerRadius: config.cornerRadius ?? UIBorderRadius.pill,
            showGlow: config.showGlow ?? true,
            showPercentage: config.showPercentage ?? false,
            ...config
        };
        
        this.progress = 0;
        this.targetProgress = 0;
        this.container = scene.add.container(x, y);
        
        this.create();
    }

    create() {
        const { width, height, cornerRadius } = this.config;
        
        // Background track
        this.track = this.scene.add.graphics();
        this.track.fillStyle(this.config.bgColor, 0.5);
        this.drawPill(this.track, -width/2, -height/2, width, height);
        this.container.add(this.track);
        
        // Progress fill
        this.fill = this.scene.add.graphics();
        this.container.add(this.fill);
        
        // Glow effect
        if (this.config.showGlow) {
            this.glow = this.scene.add.graphics();
            this.glow.setAlpha(0.5);
            this.container.add(this.glow);
        }
        
        // Percentage text
        if (this.config.showPercentage) {
            this.percentText = this.scene.add.text(width/2 + 15, 0, '0%', {
                fontSize: '14px',
                fontFamily: '"Inter", sans-serif',
                fill: '#ffffff',
                fontStyle: '600'
            }).setOrigin(0, 0.5);
            this.container.add(this.percentText);
        }
        
        // Initial draw
        this.updateVisual();
    }

    drawPill(graphics, x, y, width, height) {
        const radius = height / 2;
        
        graphics.beginPath();
        graphics.moveTo(x + radius, y);
        graphics.lineTo(x + width - radius, y);
        graphics.arc(x + width - radius, y + radius, radius, -Math.PI / 2, Math.PI / 2);
        graphics.lineTo(x + radius, y + height);
        graphics.arc(x + radius, y + radius, radius, Math.PI / 2, Math.PI * 1.5);
        graphics.closePath();
        graphics.fillPath();
    }

    updateVisual() {
        const { width, height } = this.config;
        const fillWidth = Math.max(height, width * this.progress);
        
        // Clear and redraw fill
        this.fill.clear();
        this.fill.fillStyle(this.config.fillColor, 1);
        this.drawPill(this.fill, -width/2, -height/2, fillWidth, height);
        
        // Update glow
        if (this.glow) {
            this.glow.clear();
            this.glow.fillStyle(this.config.glowColor, 0.4);
            this.drawPill(this.glow, -width/2, -height/2 - 1, fillWidth, height + 2);
        }
        
        // Update percentage text
        if (this.percentText) {
            this.percentText.setText(`${Math.round(this.progress * 100)}%`);
        }
    }

    setProgress(value, animate = true) {
        this.targetProgress = Math.max(0, Math.min(1, value));
        
        if (animate) {
            this.scene.tweens.add({
                targets: this,
                progress: this.targetProgress,
                duration: UIAnimation.normal,
                ease: UIAnimation.easeOut,
                onUpdate: () => this.updateVisual()
            });
        } else {
            this.progress = this.targetProgress;
            this.updateVisual();
        }
    }

    getProgress() {
        return this.progress;
    }

    // Indeterminate loading animation
    startIndeterminate() {
        if (this.indeterminateTween) return;
        
        const { width, height } = this.config;
        const indicatorWidth = width * 0.3;
        
        // Create sliding indicator
        this.indicator = this.scene.add.graphics();
        this.indicator.fillStyle(this.config.fillColor, 1);
        this.drawPill(this.indicator, 0, -height/2, indicatorWidth, height);
        this.indicator.x = -width/2;
        this.container.add(this.indicator);
        
        // Hide regular fill
        this.fill.setVisible(false);
        if (this.glow) this.glow.setVisible(false);
        
        // Animate
        this.indeterminateTween = this.scene.tweens.add({
            targets: this.indicator,
            x: width/2 - indicatorWidth,
            duration: 1000,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
        });
    }

    stopIndeterminate() {
        if (this.indeterminateTween) {
            this.indeterminateTween.stop();
            this.indeterminateTween = null;
        }
        
        if (this.indicator) {
            this.indicator.destroy();
            this.indicator = null;
        }
        
        this.fill.setVisible(true);
        if (this.glow) this.glow.setVisible(true);
    }

    setPosition(x, y) {
        this.container.setPosition(x, y);
    }

    setAlpha(alpha) {
        this.container.setAlpha(alpha);
    }

    setVisible(visible) {
        this.container.setVisible(visible);
    }

    setDepth(depth) {
        this.container.setDepth(depth);
    }

    destroy() {
        this.stopIndeterminate();
        this.container.destroy();
    }
}
